/**
 * The till's offline copy of the products, in IndexedDB (2026-09-26).
 *
 * It used to ride in the persisted store in localStorage, whose quota is about
 * 5 MB per site. Measured in Chrome: 8 000 products fit, 10 000 threw
 * QuotaExceededError — and zustand's persist writes on EVERY set(), so a full
 * quota throws out of every state change in the app. The offline sales queue
 * lives in the same localStorage (lib/offline.ts), so a big catalogue could
 * also make an offline sale fail to save. IndexedDB holds hundreds of MB, and
 * the copy is written here in the background, a second after the list
 * settles, instead of on every change.
 *
 * The copy carries the user and organization it belongs to and is read back
 * only when both match — a cache outlives a sign-out (the 2026-09-09
 * incident), and one user can belong to more than one shop. Every failure is
 * silent: without the copy the app works online exactly as before; only an
 * offline reload has nothing to show.
 *
 * 1.25 — the copy also carries the branch lens its quantities are for, the
 * cost visibility it was read with, the server's watermark and a `local` mark
 * (a change the server never saw), so a reload can open the till from it plus
 * one delta. What it may be used for is decided in lib/warm-products.ts.
 */
import type { Product } from './mock-data'
import type { CacheEntryV2, CacheSnapshot } from './warm-products'

const DB_NAME = 'martepos-cache'
const STORE = 'kv'
const KEY = 'products'
const DELAY_MS = 1000

/** v1 (2026-09-26 → 1.24): no version, no identity beyond user + org. */
type EntryV1 = { userId: string; orgId: string; savedAt: string; products: Product[] }

/** The product list only when the cache belongs to this exact shop login. */
export function matchingProductCache(
  entry: unknown,
  userId: string | null | undefined,
  orgId: string | null | undefined,
): Product[] | null {
  if (!userId || !orgId || !entry || typeof entry !== 'object') return null
  const candidate = entry as Partial<EntryV1>
  return candidate.userId === userId &&
    candidate.orgId === orgId &&
    Array.isArray(candidate.products)
    ? candidate.products
    : null
}

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      // Private mode on some browsers refuses IndexedDB outright.
      resolve(null)
    }
  })
}

const db = () => (dbPromise ??= openDb())

/** The stored entry as it is (v1, v2 or nothing) — lib/warm-products.ts
 *  decides what it may be used for. Never rejects. */
export async function readProductCacheEntry(): Promise<unknown> {
  const d = await db()
  if (!d) return null
  return new Promise((resolve) => {
    try {
      const req = d.transaction(STORE, 'readonly').objectStore(STORE).get(KEY)
      req.onsuccess = () => resolve(req.result ?? null)
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function write(entry: CacheEntryV2 | null): Promise<void> {
  const d = await db()
  if (!d) return
  await new Promise<void>((resolve) => {
    try {
      const tx = d.transaction(STORE, 'readwrite')
      const store = tx.objectStore(STORE)
      if (entry) store.put(entry, KEY)
      else store.delete(KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => resolve()
      tx.onabort = () => resolve()
    } catch {
      resolve()
    }
  })
}

let timer: ReturnType<typeof setTimeout> | null = null
let pending: CacheEntryV2 | null = null

/** The entry this client writes. `codes: 1` (158, 1.40) says its products
 *  carry `extra_barcodes`, `fits: 1` (187, 1.62) that they carry `fits` and
 *  `oem_codes`, `club: 1` (188, 1.63) that they carry `club_price`, `svc: 1` (196, 1.68) that they carry `system_kind`;
 *  warmCopy refuses an entry without any of them. `v` stays 2
 *  so the offline copy and older readers keep their shape. */
export function productCacheEntry(snapshot: CacheSnapshot, savedAt: string): CacheEntryV2 {
  return { v: 2, codes: 1, fits: 1, club: 1, svc: 1, savedAt, ...snapshot }
}

/** Save the list and its meta a second after the list stops changing (a sale
 *  changes it twice). The snapshot is taken NOW, so the watermark saved is
 *  the one that belonged to these products. */
export function scheduleProductCache(snapshot: CacheSnapshot): void {
  pending = productCacheEntry(snapshot, new Date().toISOString())
  if (timer) clearTimeout(timer)
  timer = setTimeout(flushProductCache, DELAY_MS)
}

/** Write what is waiting now — the tab is closing. */
export function flushProductCache(): void {
  if (timer) { clearTimeout(timer); timer = null }
  const entry = pending
  pending = null
  if (entry) void write(entry)
}

/** Sign-out / another user: nothing of this shop may stay behind. */
export function clearProductCache(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null }
  pending = null
  return write(null)
}
